export interface OutcomeFields {
  totalStrikes: number;
  significantStrikes: number;
  significantStrikesHead: number;
  significantStrikesBody: number;
  significantStrikesLegs: number;
  takedowns?: number;
  takedownAttempts?: number;
  takedownsDefended?: number;
  submissionAttempts?: number;
  knockdowns?: number;
  transitions?: number;
  elapsedControlTime: number;
  nearFalls?: number;
  exposures?: number;
  falls?: number;
  pinnings?: number;
  roles?: number;
  reversals?: number;
  turnovers?: number;
  outOfBounds?: number;
  escapes?: number;
  stallings?: number;
  passivities?: number;
  advantages?: number;
  cautions?: number;
  backtakes?: number;
  counterattacks?: number;
  technicalViolations?: number;
  score?: number;
}

export interface FighterOutcome {
  /** Id legado; `null`/`0` em lutadores sem mapeamento — não serve de chave. */
  fighterId: number | null;
  /** UUID do lutador: a chave confiável para casar o outcome com o canto. */
  fighterNewId?: string | null;
  score: number;
  standing: OutcomeFields;
  fence: OutcomeFields;
  ground: OutcomeFields;
  riding: OutcomeFields;
}

export interface FightRequest {
  id: string;
  legacyId?: number;
  winner: string | null;
  finishedAt: string | null;
  finishedAtRound: number | null;
  result: string | null;
  redCornerId?: string;
  blueCornerId?: string;
  eventId?: string;
}

export interface FightData {
  id: string;
  legacyId: number;
  promotionId: string;
  status: string;
  currentRound: number;
  currentStance: string;
  currentDomain: string | null;
  winnerId: number | null;
  winner: string | null;
  fightType: string;
  finishAt: string | null;
  finishAtRound: number | null;
  result: string | null;
  fightersOutcome: Array<FighterOutcome>;
  loading: boolean;
  redCornerId?: string;
  legacyRedCornerId?: number;
  redCornerName?: string;
  blueCornerId?: string;
  legacyBlueCornerId?: number;
  blueCornerName?: string;
  totalRound: number;
  eventId?: string;
  legacyEventId?: number;
  eventName?: string;
  currentMinutes?: number;
  currentSeconds?: number;
  roundStatus?: string;
  controlMode: boolean;
  oneFightId?: string;
  oneEventId?: string;
  /**
   * `_id` das últimas actions já contabilizadas no `fightersOutcome`. Existe só
   * para o reducer ignorar entregas duplicadas do mesmo `recorded` — a lista de
   * actions já deduplica por `_id`, o outcome não deduplicava e inflava o stat.
   */
  appliedActionIds?: Array<string>;
}

export interface UpdateActions {
  redCornerId?: number | null;
  blueCornerId?: number | null;
  redCornerIdNew?: number | null;
  blueCornerIdNew?: number | null;
  redCornerName?: string | null;
  blueCornerName?: string | null;
}

export interface FightAction {
  _id?: string;
  fightId: number;
  fightNewId?: string;
  eventId: number | string | undefined;
  boutId: string | undefined;
  corner: string;
  round: number;
  stance: string;
  domain: boolean | null;
  roundTime?: number;
  name: string;
  value?: number;
  // `null` é o empate de `fightResult`: sem vencedor, mas com o campo gravado —
  // diferente de `undefined`, que sumiria do JSON.
  authorId: number | null | undefined;
  authorNewId?: string | undefined;
  targetId: number | null | undefined;
  targetNewId?: string | undefined;
  createdAt?: Date;
  updatedAt?: Date;
  extraFields?: any | null;
  __v?: number;
}

export interface ReducerAction {
  type: string;
  payload?: FightAction;
  init?: FightData;
  updateData?: Partial<FightData>;
  time?: {
    currentMinutes: number;
    currentSeconds: number;
  };
}

export interface IIndexable {
  [key: string]: any;
}

export const getFightersOutcome = (
  fighterId: number | null,
  fighterNewId?: string | null,
) => ({
  fighterId,
  fighterNewId,
  score: 0,
  standing: {
    totalStrikes: 0,
    significantStrikes: 0,
    significantStrikesHead: 0,
    significantStrikesBody: 0,
    significantStrikesLegs: 0,
    takedowns: 0,
    takedownAttempts: 0,
    takedownsDefended: 0,
    submissionAttempts: 0,
    knockdowns: 0,
    elapsedControlTime: 0,
    nearFalls: 0,
    exposures: 0,
    falls: 0,
    pinnings: 0,
    roles: 0,
    reversals: 0,
    turnovers: 0,
    outOfBounds: 0,
    escapes: 0,
    stallings: 0,
    passivities: 0,
    advantages: 0,
    cautions: 0,
    backtakes: 0,
    counterattacks: 0,
    technicalViolations: 0,
    score: 0,
  },
  ground: {
    totalStrikes: 0,
    significantStrikes: 0,
    significantStrikesHead: 0,
    significantStrikesBody: 0,
    significantStrikesLegs: 0,
    transitions: 0,
    submissionAttempts: 0,
    elapsedControlTime: 0,
    nearFalls: 0,
    exposures: 0,
    falls: 0,
    pinnings: 0,
    roles: 0,
    reversals: 0,
    turnovers: 0,
    outOfBounds: 0,
    escapes: 0,
    stallings: 0,
    passivities: 0,
    advantages: 0,
    cautions: 0,
    backtakes: 0,
    counterattacks: 0,
    technicalViolations: 0,
    score: 0,
  },
  fence: {
    totalStrikes: 0,
    significantStrikes: 0,
    significantStrikesHead: 0,
    significantStrikesBody: 0,
    significantStrikesLegs: 0,
    takedowns: 0,
    takedownAttempts: 0,
    takedownsDefended: 0,
    submissionAttempts: 0,
    knockdowns: 0,
    elapsedControlTime: 0,
  },
  riding: {
    totalStrikes: 0,
    significantStrikes: 0,
    significantStrikesHead: 0,
    significantStrikesBody: 0,
    significantStrikesLegs: 0,
    transitions: 0,
    submissionAttempts: 0,
    elapsedControlTime: 0,
    nearFalls: 0,
    exposures: 0,
    falls: 0,
    pinnings: 0,
    roles: 0,
    reversals: 0,
    turnovers: 0,
    outOfBounds: 0,
    escapes: 0,
    stallings: 0,
    passivities: 0,
    advantages: 0,
    cautions: 0,
    backtakes: 0,
    counterattacks: 0,
    technicalViolations: 0,
    score: 0,
  },
});

export const initData = {
  id: '',
  legacyId: 0,
  eventId: '',
  legacyEventId: 1,
  promotionId: '',
  status: 'inactive',
  currentRound: 1,
  currentStance: 'standing',
  currentDomain: null,
  loading: false,
  winnerId: null,
  winner: null,
  fightType: '',
  result: '',
  finishAt: null,
  finishAtRound: null,
  finishType: null,
  redCornerId: '',
  blueCornerId: '',
  legacyRedCornerId: 4678,
  legacyBlueCornerId: 3544,
  totalRound: 1,
  currentMinutes: 0,
  currentSeconds: 0,
  controlMode: true,
  appliedActionIds: [] as Array<string>,
  fightersOutcome: [
    {
      fighterId: 4678,
      score: 0,
      standing: {
        totalStrikes: 0,
        significantStrikes: 0,
        significantStrikesHead: 0,
        significantStrikesBody: 0,
        significantStrikesLegs: 0,
        takedowns: 0,
        takedownAttempts: 0,
        takedownsDefended: 0,
        submissionAttempts: 0,
        knockdowns: 0,
        elapsedControlTime: 0,
        nearFalls: 0,
        exposures: 0,
        falls: 0,
        pinnings: 0,
        roles: 0,
        reversals: 0,
        turnovers: 0,
        outOfBounds: 0,
        escapes: 0,
        stallings: 0,
        passivities: 0,
        advantages: 0,
        transitions: 0,
        cautions: 0,
        backtakes: 0,
        counterattacks: 0,
        technicalViolations: 0,
        score: 0,
      },
      ground: {
        totalStrikes: 0,
        significantStrikes: 0,
        significantStrikesHead: 0,
        significantStrikesBody: 0,
        significantStrikesLegs: 0,
        transitions: 0,
        submissionAttempts: 0,
        elapsedControlTime: 0,
        nearFalls: 0,
        exposures: 0,
        falls: 0,
        pinnings: 0,
        roles: 0,
        reversals: 0,
        turnovers: 0,
        outOfBounds: 0,
        escapes: 0,
        stallings: 0,
        passivities: 0,
        advantages: 0,
        cautions: 0,
        backtakes: 0,
        counterattacks: 0,
        technicalViolations: 0,
        score: 0,
      },
      fence: {
        totalStrikes: 0,
        significantStrikes: 0,
        significantStrikesHead: 0,
        significantStrikesBody: 0,
        significantStrikesLegs: 0,
        takedowns: 0,
        takedownAttempts: 0,
        takedownsDefended: 0,
        submissionAttempts: 0,
        knockdowns: 0,
        elapsedControlTime: 0,
      },
      riding: {
        totalStrikes: 0,
        significantStrikes: 0,
        significantStrikesHead: 0,
        significantStrikesBody: 0,
        significantStrikesLegs: 0,
        transitions: 0,
        submissionAttempts: 0,
        elapsedControlTime: 0,
        nearFalls: 0,
        exposures: 0,
        falls: 0,
        pinnings: 0,
        roles: 0,
        reversals: 0,
        turnovers: 0,
        outOfBounds: 0,
        escapes: 0,
        stallings: 0,
        passivities: 0,
        advantages: 0,
        cautions: 0,
        backtakes: 0,
        counterattacks: 0,
        technicalViolations: 0,
        score: 0,
      },
    },
    {
      fighterId: 3544,
      score: 0,
      standing: {
        totalStrikes: 0,
        significantStrikes: 0,
        significantStrikesHead: 0,
        significantStrikesBody: 0,
        significantStrikesLegs: 0,
        takedowns: 0,
        takedownAttempts: 0,
        takedownsDefended: 0,
        submissionAttempts: 0,
        knockdowns: 0,
        elapsedControlTime: 0,
        nearFalls: 0,
        exposures: 0,
        falls: 0,
        pinnings: 0,
        roles: 0,
        reversals: 0,
        turnovers: 0,
        outOfBounds: 0,
        escapes: 0,
        stallings: 0,
        passivities: 0,
        transitions: 0,
        advantages: 0,
        cautions: 0,
        backtakes: 0,
        counterattacks: 0,
        technicalViolations: 0,
        score: 0,
      },
      ground: {
        totalStrikes: 0,
        significantStrikes: 0,
        significantStrikesHead: 0,
        significantStrikesBody: 0,
        significantStrikesLegs: 0,
        transitions: 0,
        submissionAttempts: 0,
        elapsedControlTime: 0,
        nearFalls: 0,
        exposures: 0,
        falls: 0,
        pinnings: 0,
        roles: 0,
        reversals: 0,
        turnovers: 0,
        outOfBounds: 0,
        escapes: 0,
        stallings: 0,
        passivities: 0,
        advantages: 0,
        cautions: 0,
        backtakes: 0,
        counterattacks: 0,
        technicalViolations: 0,
        score: 0,
      },
      fence: {
        totalStrikes: 0,
        significantStrikes: 0,
        significantStrikesHead: 0,
        significantStrikesBody: 0,
        significantStrikesLegs: 0,
        takedowns: 0,
        takedownAttempts: 0,
        takedownsDefended: 0,
        submissionAttempts: 0,
        knockdowns: 0,
        elapsedControlTime: 0,
      },
      riding: {
        totalStrikes: 0,
        significantStrikes: 0,
        significantStrikesHead: 0,
        significantStrikesBody: 0,
        significantStrikesLegs: 0,
        transitions: 0,
        submissionAttempts: 0,
        elapsedControlTime: 0,
        nearFalls: 0,
        exposures: 0,
        falls: 0,
        pinnings: 0,
        roles: 0,
        reversals: 0,
        turnovers: 0,
        outOfBounds: 0,
        escapes: 0,
        stallings: 0,
        passivities: 0,
        advantages: 0,
        cautions: 0,
        backtakes: 0,
        counterattacks: 0,
        technicalViolations: 0,
        score: 0,
      },
    },
  ],
};
